import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Check, Feather } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { WizardShell } from "@/components/tuw/post/WizardShell"
import { PriceRail } from "@/components/tuw/post/PriceRail"
import { STEPPER_COLLAPSE } from "@/components/tuw/post/Stepper"
import { FieldRenderer } from "@/components/tuw/post/FieldRenderer"
import { countWords, visibleFieldsOf, isEmpty, mergeDefaults, fieldOk, stepIsValid, fieldError } from "@/lib/post/fieldValidation"
import { AddonStep } from "@/components/tuw/post/AddonStep"
import { EntryPicker } from "@/components/tuw/post/EntryPicker"
import EntrySplit from "@/components/tuw/post/EntrySplit"
import { ServiceSetupHub } from "@/components/tuw/post/ServiceSetupHub"
import { ReviewSummary } from "@/components/tuw/post/ReviewSummary"
import { PostedConfirmation } from "@/components/tuw/post/PostedConfirmation"

import { canCopyBookDetailsTo, copyBookDetailsSnapshot } from "@/lib/post/bookDetails"
import { useDraftSave } from "@/lib/post/useDraftSave"
import { useBundleUploadStatus } from "@/lib/post/useBundleUploadStatus"
import { acknowledgeFiles, omitSavedBundleFiles, sentFiles } from "@/lib/post/acknowledgeUploads"
import { SERVICE_CONFIGS } from "@/lib/post/services"
import { resolveIssueTarget } from "@/lib/post/issueNavigation"
import { serializeForSave, serializeBundleForSave, hydrateFromDraft } from "@/lib/post/serialize"
import {
  bundleQueue,
  bundleComposition,
  bundlePricePayload,
  bundleEstimateLines,
  bundleDeliverables,
  bundleDeliverablesValid,
  PLATINUM,
} from "@/lib/post/bundle"
import {
  createDraft,
  saveDraft,
  submitProject,
  priceProject,
  addDiscount,
  removeDiscount,
} from "@/lib/postProjectActions"
import {
  isQaQcEligible,
  managedAllowed as managedAllowedFor,
  hasAddonChoice,
  managedTiersFor,
  addonLineAmounts,
  addonEstimate,
  addonPriceFields,
} from "@/lib/post/addon"
import * as options from "@/lib/post/options"

// PostProject — the Atrium Stage 7 revenue-path posting builder (Wave III). This
// page owns the whole client-side state machine and wires the already-built
// engine, configs and actions together. The flow follows the design's 3-node
// stepper (Path → Select → Set up):
//
//   entry  — EntryPicker's two steps: Path (1) then Select (2)
//   hub    — "Set up your services" (the third node): a hub-and-spoke launchpad.
//            From here each service is configured in turn; finishing one returns
//            here. For a bundle it also collects the bundle materials.
//   service — one service's own wizard (its own per-service stepper)
//   addon  — Management & review add-ons
//   review — final summary + submit
//
// See tasks/post-a-project/INTEGRATION-NOTES.md for the seam this assembles.

// ── small pure helpers ───────────────────────────────────────────────────────

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

function sameSet(a = [], b = []) {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((x) => set.has(x))
}

function focusPostField(nodes, fieldKey) {
  const node = nodes.get(fieldKey) || document.querySelector(`[data-post-field="${fieldKey}"]`)
  if (!node) return

  node.scrollIntoView({ behavior: "smooth", block: "center" })
  const control = node.querySelector("input, textarea, button, [tabindex]:not([tabindex='-1'])")
  control?.focus({ preventScroll: true })
}

// The Silver/Gold outline step's "Upgrade To Our Platinum Bundle" choice.
function upgradesToPlatinum(svc, key, value, state) {
  return svc === "book_writing" && key === "add_outline" && value === true &&
    !!state.bundle && !bundleComposition(state.bundleTier).outline
}

// The draft as Platinum, keeping the manuscript the customer configured. The
// tier drives the children, rail and review; add_outline never stays true on a
// bundle (the server's add_outline would rebuild every child posting), and a
// saved bring-your-own outline is detached (null saves as the empty value), as
// the old wizard purged it. The applied code is the server's to keep or drop.
function platinumUpgradeOf(state) {
  const bw = state.postings.book_writing || {}
  return {
    ...state,
    bundleTier: PLATINUM,
    selected: state.selected.includes("outline") ? state.selected : [...state.selected, "outline"],
    postings: { ...state.postings, book_writing: { ...bw, add_outline: undefined, file: null } },
  }
}

const EMPTY_STATE = {
  draftId: null,
  projectName: "",
  path: null,
  bundle: false,
  bundleTier: null,
  selected: [],
  stage: "entry",
  queueIndex: 0,
  stepIndex: 0,
  // Where a hub/review "Set up" or "Edit" jump should return to once the service
  // (or add-on step) is done: the hub by default, "review" when jumped from Review.
  returnStage: "hub",
  postings: {},
  bundleExtras: { collaborative: true, character_voice_count: null, files: {} },
  managed_services_tier: "self_managed",
  managed_service_details: {},
  // Default to an explicit "declined" — QA/QC are opt-in, and submit's
  // quality_assurance__one requires a non-null selection for QA-eligible work
  // (book_writing, the bundle manuscript, etc.).
  quality_assurance_addon: "declined",
  quality_control_enabled: "declined",
  discount: null,
  serverCost: null,
  // Set by a launchpad deep-link (/post?service=…): renders the entry stage as
  // the lean "name & launch" focused arrival instead of the full picker.
  focusedService: null,
  repeat: false,
}

// A returning-customer launchpad tile pre-fills the project name with a sensible
// per-type default ("Cover Design - June 2026"); the customer edits it freely.
function monthYear() {
  return new Date().toLocaleString("en-US", { month: "long", year: "numeric" })
}

function repeatDefaultName(catalog, serviceKey) {
  const svc = (catalog?.services || []).find((s) => s.key === serviceKey)
  return svc ? `${svc.name} - ${monthYear()}` : ""
}

// Hydrate the initial wizard state from a resumed draft (or start clean). On
// resume we jump to the persisted cursor (currentStepApi); otherwise the service
// stage at the top of the queue. With no draft, a launchpad `prefill` deep-link
// preselects the entry picker's path + service (the controller validated it).
function initialStateFrom(catalog, prefill) {
  const draft = catalog?.draft
  if (!draft) {
    if (prefill?.path === "bundle") return { ...EMPTY_STATE, path: "bundle" }
    if (prefill?.path === "byo" && prefill.service) {
      return {
        ...EMPTY_STATE,
        path: "byo",
        selected: [prefill.service],
        focusedService: prefill.service,
        repeat: !!prefill.repeat,
        projectName: prefill.repeat ? repeatDefaultName(catalog, prefill.service) : "",
      }
    }
    return { ...EMPTY_STATE }
  }

  const hydrated = hydrateFromDraft(draft, SERVICE_CONFIGS) || { ...EMPTY_STATE }
  const cursor = draft.currentStepApi || {}

  // A resumed draft can carry a Managed selection the gates no longer offer
  // (the tier was chosen before the SEO order shrank below ten articles, or
  // saved before this normalisation existed). Drop it to self_managed: the
  // server skips its tier-validity check exactly when the tier is ineligible
  // (valid_step? gates managed_services__one on eligible_for_managed_services?),
  // so a stale "managed" would post and bill a service the wizard never shows.
  const staleManaged =
    hydrated.managed_services_tier === "managed" &&
    !managedAllowedFor(hydrated.selected, hydrated)

  // Resume lands on the hub (the orchestrator) unless the customer had reached
  // the add-on or review stage. The hub lets them jump back into any service, so
  // a mid-service or legacy "deliverables" cursor resolves cleanly to "hub". The
  // add-on stage only exists when it holds a real choice (AU-26 — flag on, and
  // Managed available or QA/QC addable), so a stale addon cursor normalises
  // FORWARD to review: the customer had already cleared the hub to park there.
  const hasAddon = hasAddonChoice(catalog?.managedServices, hydrated.selected, hydrated)
  const resumeStage =
    cursor.stage === "review" || (cursor.stage === "addon" && hasAddon)
      ? cursor.stage
      : cursor.stage === "addon"
        ? "review"
        : "hub"

  return {
    ...hydrated,
    ...(staleManaged ? { managed_services_tier: "self_managed" } : {}),
    stage: resumeStage,
    queueIndex: cursor.queueIndex ?? 0,
    stepIndex: cursor.stepIndex ?? 0,
    discount: draft.discount ?? null,
  }
}

// ── config walking ───────────────────────────────────────────────────────────

function getServiceSteps(svc, values, ctx) {
  const config = SERVICE_CONFIGS[svc]
  if (!config) return []
  const steps = typeof config.steps === "function" ? config.steps(values, ctx) : config.steps
  const arr = Array.isArray(steps) ? steps : []
  return arr.filter((step) => !step.when || step.when(values, ctx))
}

// Faithful labels for the macro (non-service) stages, taken from each stage's
// own wizard copy. Service stages carry per-step titles; the macro stages are
// single conceptual steps.
const MACRO_STEP_LABELS = {
  entry: "Choose services",
  hub: "Set up your services",
  addon: "Management & review",
  review: "Review & post",
}

// Resolve the parked cursor into a human { stepLabel, stepNum, stepTotal } for
// the dashboard resume-your-draft banner (Addendum A / CR-001). A service stage
// reads the wizard's OWN visible-step model via getServiceSteps — the exact walk
// the render uses (queue derivation + step.when filtering) so the stamped total
// and label never drift from what the customer sees (tuw-app:P-007). The macro
// stages are single conceptual steps (1 of 1). This value is persisted into
// current_step_api; the presenter reads it back instead of re-deriving the
// client-only step tree server-side.
function resolveCursorStamp(state, catalog, ctx) {
  if (state.stage === "service") {
    const order = (catalog.services || []).map((s) => s.key)
    const queue = state.bundle
      ? bundleQueue().filter((key) => SERVICE_CONFIGS[key])
      : order.filter((key) => (state.selected || []).includes(key) && SERVICE_CONFIGS[key])
    const svc = queue[Math.min(state.queueIndex ?? 0, Math.max(queue.length - 1, 0))]
    const values = (svc && state.postings[svc]) || {}
    const steps = svc ? getServiceSteps(svc, values, ctx) : []
    if (steps.length) {
      const idx = Math.min(state.stepIndex ?? 0, steps.length - 1)
      return { stepLabel: steps[idx].title, stepNum: idx + 1, stepTotal: steps.length }
    }
  }

  return { stepLabel: MACRO_STEP_LABELS[state.stage] || MACRO_STEP_LABELS.hub, stepNum: 1, stepTotal: 1 }
}

// managed demands the full questionnaire + both waivers before review.
function addonIsValid(state) {
  if ((state.managed_services_tier || "self_managed") !== "managed") return true

  const d = state.managed_service_details || {}
  const required = [
    "freelancer_selection",
    "freelancer_note",
    "pre_scheduling",
    "progress_updates",
    "zoom_call",
    "additional_services",
  ]
  const filled = required.every((k) => d[k] !== undefined && d[k] !== null && d[k] !== "")
  const waivers = !!d.quality_waiver && !!d.timeframe_waiver

  return filled && waivers
}

// ── pricing ──────────────────────────────────────────────────────────────────

// Instant client-side estimate for the rail. BYO sums each configured service's
// estimate; a bundle shows the server "from" floor + paperback line (we never
// recompute bundle floors). Add-on lines layer on top either way.
function estimateLines(state, ctx, catalog, queue) {
  const lines = []
  let isRange = false

  if (state.bundle) {
    const est = bundleEstimateLines(state, ctx)
    if (est.lines?.length) lines.push(...est.lines)
    if (est.isRange) isRange = true
  } else {
    for (const svc of queue) {
      const config = SERVICE_CONFIGS[svc]
      const est = config?.estimate?.(state.postings[svc] || {}, ctx)
      if (est?.lines?.length) lines.push(...est.lines)
      if (est?.isRange) isRange = true
    }
  }

  const addon = addonEstimate(state, state.selected, ctx)
  if (addon?.lines?.length) lines.push(...addon.lines)

  return { lines, isRange }
}

// The server-authoritative projects array. Add-on fields merge into EVERY entry
// (the calculator reads managed/QA/QC per project). A bundle is a single entry.
function buildPricePayloads(state, ctx, queue) {
  const addon = addonPriceFields(state)

  if (state.bundle) {
    const entry = bundlePricePayload(state)
    return entry ? [{ ...entry, ...addon }] : []
  }

  return queue
    .map((svc) => SERVICE_CONFIGS[svc]?.pricePayload?.(state.postings[svc] || {}, ctx))
    .filter(Boolean)
    .map((entry) => ({ ...entry, ...addon }))
}

// Whole-order discount args. INTEGRATION-NOTES: keys are snake_case top-level
// (the controller reads params[:discount_type]). `percentage` is a boolean flag;
// `value` holds the amount in both cases (the percent number for percentage codes).
export function discountArgs(discount) {
  if (!discount) return {}
  if (discount.percentage && discount.value) return { discount_type: "percentage", discount_value: discount.value }
  if (discount.value) return { discount_type: "fixed", discount_value: discount.value }
  return {}
}

function buildRailProps(state, ctx, catalog, queue) {
  const { lines, isRange } = estimateLines(state, ctx, catalog, queue)
  const estimateTotal = round2(lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0))

  const server = state.serverCost
  const hasServerPrice = server && Number(server.total) > 0
  const total = hasServerPrice ? Number(server.totalDiscounted) : estimateTotal

  const discountNote = state.discount ? `${state.discount.code || "Discount"} applied` : undefined

  return {
    lines,
    total,
    isRange,
    fromLabel: "From",
    discountNote,
    note: "Delivery estimate shared once a freelancer is attached.",
  }
}

// ── review summary detail ─────────────────────────────────────────────────────

function serviceDetail(values = {}) {
  const parts = []
  if (values.package_type) parts.push(values.package_type)
  if (values.tier) parts.push(values.tier)
  if (values.formatting_type) parts.push(values.formatting_type)
  if (values.language) parts.push(values.language)
  if (values.editing_style) parts.push(values.editing_style)

  const wc = Number(values.word_count)
  if (wc > 0) parts.push(`${wc.toLocaleString()} words`)

  const ac = Number(values.article_count)
  if (ac > 0) parts.push(`${ac} article${ac === 1 ? "" : "s"}`)

  const ic = Number(values.illustration_count)
  if (ic > 0) parts.push(`${ic} illustration${ic === 1 ? "" : "s"}`)

  return parts.join(" · ")
}

// Per-field review value formatting (design service.html `reviewRow`, L477-488).
// Returns a display string for one answered field, or null to omit the row (an
// unanswered/hidden field). Presentation only — reads the same values the
// FieldRenderer wrote; the persisted tile/palette values pass through as-is.
function formatReviewValue(field, values, ctx) {
  const raw = values[field.key]

  switch (field.type) {
    case "note":
    case "sliders":
      return null

    case "toggle": {
      if (!raw) return null
      const detail = field.detailsKey ? values[field.detailsKey] : null
      return detail ? `Yes · ${detail}` : "Yes"
    }

    case "checkgroup":
    case "gallery":
    case "palette": {
      const arr = Array.isArray(raw) ? raw : []
      return arr.length ? arr.join(", ") : null
    }

    case "wordcount": {
      const n = Number(raw)
      return n > 0 ? `${n.toLocaleString()} words` : null
    }

    case "count": {
      const n = Number(raw)
      if (!(n > 0)) return null
      return field.unit ? `${n} ${field.unit}` : String(n)
    }

    case "file":
      return raw && raw.name ? raw.name : null

    case "consent": {
      const carrier = field.key || "ai_consent"
      const v = values[carrier]
      if (!v) return null
      const labels = (ctx.options && ctx.options.AI_CONSENT_LABELS) || {}
      return labels[v] || v
    }

    case "radio": {
      if (isEmpty(raw)) return null
      const opts = typeof field.options === "function" ? field.options(values, ctx) : field.options
      const o = (Array.isArray(opts) ? opts : []).find((x) => x.value === raw)
      return o ? o.label : raw
    }

    case "triggerWarning": {
      if (!values.trigger_warning) return null
      const cats = Array.isArray(values.trigger_warning_warnings) ? values.trigger_warning_warnings : []
      const parts = [...cats]
      if (!isEmpty(values.trigger_warning_other)) parts.push(values.trigger_warning_other)
      return parts.length ? parts.join(", ") : "Yes"
    }

    default:
      return isEmpty(raw) ? null : String(raw)
  }
}

// The design's field-level review audit: walk a service's visible steps/fields
// and emit a labeled value row per answered field (WI-A13). Reuses the existing
// config walk (getServiceSteps / visibleFieldsOf) rather than a parallel
// description of the tree (tuw-app:P-007).
function serviceReviewFields(svc, values, ctx) {
  const rows = []

  for (const step of getServiceSteps(svc, values, ctx)) {
    for (const field of visibleFieldsOf(step, values, ctx)) {
      const label = field.reviewLabel || field.label || (field.type === "consent" ? "AI use" : null)
      if (!label) continue

      const value = formatReviewValue(field, values, ctx)
      if (value == null || value === "") continue

      rows.push({ label, value })
    }
  }

  return rows
}

// Pick the right serializer for the wire payload: a bundle persists into the same
// nested attributes via a dedicated path (manuscript→cover inheritance + derived
// outline/narration children); BYO serializes the selected configs.
function serializePayload(snapshot, ctx, catalog = {}) {
  const stamp = resolveCursorStamp(snapshot, catalog, ctx)
  return snapshot.bundle
    ? serializeBundleForSave(snapshot, SERVICE_CONFIGS, ctx, stamp)
    : serializeForSave(snapshot, SERVICE_CONFIGS, ctx, stamp)
}

// ── the page ─────────────────────────────────────────────────────────────────

export default function PostProject({ catalog = {}, prefill = null, drafts = [] }) {
  const [state, setState] = useState(() => initialStateFrom(catalog, prefill))
  // S-POST-ENTRY: the split entry (Continue a draft / Start new) is the entry
  // stage's first view when the customer holds ≥1 draft and there's no deep-link
  // resume/prefill (state E1). "Start something new" dismisses it to reveal the
  // existing EntryPicker path/select flow (E2's picker). A deep-link (prefill) or
  // a by-id resume (catalog.draft) bypasses the split entirely (E3) — the
  // controller already routed those, so it just never mounts here.
  const [splitDismissed, setSplitDismissed] = useState(false)
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [savingExit, setSavingExit] = useState(false)
  const [submitErrors, setSubmitErrors] = useState(null)
  const [submitIssues, setSubmitIssues] = useState([])
  // The finish-profile path a refused submit names when billing details are missing.
  const [submitFinishProfile, setSubmitFinishProfile] = useState(null)
  const [discountBusy, setDiscountBusy] = useState(false)
  const [discountError, setDiscountError] = useState(null)
  // The server confirmation payload stashed on a successful submit; drives the
  // posted-confirmation stage (WI-B2). Null until the project is posted.
  const [posted, setPosted] = useState(null)
  // WI-A20 inline-error idiom: Continue is always clickable on a service step;
  // pressing it on an invalid step flips showErr so each unmet field renders its
  // message. Cleared on any step/stage/service change (the effect below).
  const [showErr, setShowErr] = useState(false)
  const fieldNodesRef = useRef(new Map())
  const pendingFieldFocusRef = useRef(null)
  const wizardTopRef = useRef(null)

  // ctx — built once, threaded into every config fn, FieldRenderer, AddonStep and
  // serialize. Rebuilt only when the add-on tier or the selection changes.
  const ctx = useMemo(
    () => ({
      insider: catalog.insider,
      canUseAi: catalog.canUseAi,
      canPostNarration: catalog.canPostNarration,
      // Feature gate for the Management & review stage (bug 11) + PriceList-driven
      // outline card prices (bug 13) — both threaded to configs/FieldRenderer.
      managedServices: catalog.managedServices,
      outlinePrices: catalog.outlinePrices,
      // Per-unit server rates (Insider-aware); configs read ctx.rates?.X ?? RATES.X
      // so every displayed price traces to the customer's PriceList (WI-C1/C2).
      rates: catalog.rates,
      managedTier: state.managed_services_tier,
      selected: state.selected,
      // bundle flags drive the reused configs (hide inline outline + inherited cover
      // fields; force the manuscript-fiction lock for the Ultimate Fiction bundle).
      bundle: state.bundle,
      bundleForceFiction: state.bundle && bundleComposition(state.bundleTier).forceFiction,
      // Silver/Gold carry no outline, so their manuscript asks for one (or the
      // Platinum upgrade, priced from that bundle's own catalog card).
      bundleIncludesOutline: state.bundle && !!bundleComposition(state.bundleTier).outline,
      upgradeBundle: (catalog.bundles || []).find((b) => b.bundleTier === PLATINUM),
      options,
    }),
    [
      catalog.insider,
      catalog.canUseAi,
      catalog.canPostNarration,
      catalog.managedServices,
      catalog.outlinePrices,
      catalog.rates,
      catalog.bundles,
      state.managed_services_tier,
      state.selected,
      state.bundle,
      state.bundleTier,
    ]
  )

  // The configurable queue. A bundle drives a fixed queue (book_writing + cover_design,
  // the children the customer configures directly); BYO uses the selected services in
  // catalog order. outline/narration/formatting/description are bundle-derived.
  const queue = useMemo(() => {
    if (state.bundle) return bundleQueue().filter((key) => SERVICE_CONFIGS[key])
    const order = (catalog.services || []).map((s) => s.key)
    return order.filter((key) => state.selected.includes(key) && SERVICE_CONFIGS[key])
  }, [catalog.services, state.selected, state.bundle])

  const queueFromSelected = (selected) => {
    const order = (catalog.services || []).map((s) => s.key)
    return order.filter((key) => selected.includes(key) && SERVICE_CONFIGS[key])
  }

  // keep the latest state/ctx reachable from debounced callbacks
  const stateRef = useRef(state)
  stateRef.current = state
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx
  const explicitSaveRef = useRef(false)
  const acknowledgedBundleFilesRef = useRef({})
  // The Platinum upgrade's save is in flight: the wizard is held until the
  // server answers (navigation, Save & exit and Post included).
  const [upgradePending, setUpgradePending] = useState(false)
  const upgradePendingRef = useRef(false)
  // A discount Apply/Remove is in flight. Neither it nor the upgrade, Post or
  // Save & exit starts while the other is out, so a discount answer always
  // describes the current bundle and never lands on a posted project.
  const discountBusyRef = useRef(false)
  const {
    statuses: bundleUploadStatuses,
    noteFileChange: noteBundleFileChange,
    uploadStarted,
    uploadSaved,
    uploadFailed,
  } = useBundleUploadStatus(state.bundleExtras?.files)
  // File replacements are sensitive: a later save can purge the previous
  // blob while submit is moving it into an Order. The shared queue serializes
  // autosave, final save, and Save & exit, even after a failed request.
  const { savedAt, saveError, retryingSave, noteDraftEdit, saveCurrentDraft, retrySave } = useDraftSave({
    saveDraft,
    getSnapshot: () => stateRef.current,
    getPayload: (snapshot) => serializePayload(snapshot, ctxRef.current, catalog),
    preparePayload: (payload) => omitSavedBundleFiles(payload, acknowledgedBundleFilesRef.current),
    onSaving: uploadStarted,
    onSaved: (snapshot, payload) => {
      uploadSaved(snapshot, payload)
      const sent = sentFiles(snapshot, payload)
      for (const [slot, file] of sent.bundle) acknowledgedBundleFilesRef.current[slot] = file
      if (sent.postings.length || sent.bundle.length) {
        setState((prev) => acknowledgeFiles(prev, sent))
      }
    },
    onFailed: uploadFailed,
    canRetry: () => !explicitSaveRef.current,
  })

  // Clear the inline-error flag whenever the customer moves to a different step,
  // service, or stage (WI-A20). A Continue press on an invalid step sets showErr
  // without changing these, so the errors stay visible until they navigate.
  //
  // That same move re-anchors the viewport (UPDA-77). The step swaps in place
  // with no Inertia visit, so nothing resets the shell's scroller: a customer who
  // scrolled to Continue at the bottom of a tall step landed at the bottom of the
  // next one, which on a phone reads as the page opening half way down. A queued
  // field focus keeps the scroll instead — that jump exists to put one invalid
  // field on screen and racing it to the top would undo it.
  useEffect(() => {
    const pendingField = pendingFieldFocusRef.current
    setShowErr(!!pendingField)

    if (pendingField) {
      window.requestAnimationFrame(() => {
        focusPostField(fieldNodesRef.current, pendingField)
        pendingFieldFocusRef.current = null
      })
      return
    }

    // "instant" is explicit rather than the "auto" default so that adding
    // `scroll-behavior: smooth` to a stylesheet later cannot silently turn this
    // into an interruptible animation — the same reasoning as OrderDetail's tab
    // scroll. The window itself never scrolls (the shell is h-screen
    // overflow-hidden); this reaches AppShell's <main>, the one scroller the app
    // shares, which spec 84 pins.
    wizardTopRef.current?.scrollIntoView({ behavior: "instant", block: "start" })
  }, [state.stage, state.queueIndex, state.stepIndex])

  // ── autosave (debounced) ───────────────────────────────────────────────────
  useEffect(() => {
    if (!state.draftId) return
    if (state.stage === "entry" || state.stage === "posted") return
    if (submitting) return

    const handle = setTimeout(() => {
      if (upgradePendingRef.current) return
      saveCurrentDraft()
        .catch(() => {})
    }, 600)

    return () => clearTimeout(handle)
  }, [state, submitting])

  // ── server price reconcile (debounced) ─────────────────────────────────────
  const pricePayloads = useMemo(() => buildPricePayloads(state, ctx, queue), [state, ctx, queue])
  const discount = useMemo(() => discountArgs(state.discount), [state.discount])
  const priceSignature = useMemo(() => JSON.stringify({ p: pricePayloads, d: discount }), [pricePayloads, discount])

  useEffect(() => {
    if (state.stage === "entry") return
    if (!pricePayloads.length) return

    let cancelled = false
    const handle = setTimeout(async () => {
      const result = await priceProject(pricePayloads, discount)
      if (!cancelled) setState((prev) => ({ ...prev, serverCost: result }))
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceSignature])

  // ── field / add-on setters ──────────────────────────────────────────────────

  const setPosting = (svc, key, value) => {
    if (upgradePendingRef.current) return
    noteDraftEdit()
    if (upgradesToPlatinum(svc, key, value, stateRef.current)) {
      upgradeToPlatinum()
      return
    }
    setState((prev) => {
      const prevSub = prev.postings[svc] || {}
      const nextSub = { ...prevSub, [key]: value }

      // Book Writing guided-flow reset cascade (mirrors the design's dependent steps).
      // The manuscript (use_ai) and outline (add_outline) steps hide when an earlier
      // choice changes, so clear the now-orphaned values instead of letting a stale
      // use_ai=true or add_outline=true strand on a hidden step. A stranded add_outline
      // is the dangerous one: save-draft persists it to the column and handle_addons
      // then mints a phantom outline_posting (+$300/+$420) the customer can't see or
      // remove. Only cascade on a real value change so re-confirming a choice is a no-op.
      if (svc === "book_writing" && value !== prevSub[key]) {
        if (key === "fiction") {
          nextSub.use_ai = false
          nextSub.add_outline = undefined
          nextSub.file = undefined
        } else if (key === "use_ai" && value) {
          nextSub.add_outline = undefined
          nextSub.file = undefined
        }
      }

      // Humanize AI has the same fiction→use_ai dependency: the use_ai step only
      // renders for non-fiction, so switching to fiction must clear a stale use_ai
      // (Plumbline bug 4). Otherwise the consent step stays hidden on the fiction
      // path and submit 422s / posts with no consent. Humanize use_ai round-trips
      // as the string "false"/"true" through the engine.
      if (svc === "humanize_ai" && key === "fiction" && value !== prevSub[key]) {
        nextSub.use_ai = "false"
      }

      const nextState = {
        ...prev,
        postings: { ...prev.postings, [svc]: nextSub },
      }

      // Managed-gate cascade (AU-26), same idiom as the outline reset above:
      // shrinking an SEO order below ten articles closes the Managed gate — and
      // can suppress the whole Management & review stage — so a previously
      // chosen Managed tier would strand invisibly. The server skips its
      // tier-validity check exactly when the tier is ineligible, so the strand
      // would post and bill. Drop it to self_managed the moment the gate shuts.
      if (
        svc === "seo" &&
        key === "article_count" &&
        nextState.managed_services_tier === "managed" &&
        !managedAllowedFor(nextState.selected, nextState)
      ) {
        nextState.managed_services_tier = "self_managed"
      }

      return nextState
    })
  }

  const setAddonField = (key, value) => {
    noteDraftEdit()
    if (key.startsWith("managed_service_details.")) {
      const sub = key.slice("managed_service_details.".length)
      setState((prev) => ({
        ...prev,
        managed_service_details: { ...(prev.managed_service_details || {}), [sub]: value },
      }))
      return
    }

    setState((prev) => ({ ...prev, [key]: value }))
  }

  // ── entry → draft ───────────────────────────────────────────────────────────

  // Whether the Management & review add-on stage is offered at all: the
  // customer_feature.managed_services flag (bug 11 / ruling C9) AND a real
  // choice on the step for this selection — Managed available, or QA/QC
  // addable (AU-26). Recomputed live: configuring SEO below ten articles on
  // the hub suppresses the stage and the wizard skips from hub to review.
  const hasAddon = hasAddonChoice(catalog.managedServices, state.selected, state)

  // After launch the customer lands on the "Set up your services" hub (the design's
  // third stepper node). With nothing selected at all, skip straight to add-ons
  // (or review, when the add-on stage is gated off). Reads the launched snapshot,
  // not this render's state — the selection it decides for is the new one.
  const goAfterLaunch = (snapshot) => {
    const nextQueue = snapshot.bundle ? bundleQueue() : queueFromSelected(snapshot.selected || [])
    const emptyStage = hasAddonChoice(catalog.managedServices, snapshot.selected, snapshot)
      ? "addon"
      : "review"
    return { stage: nextQueue.length ? "hub" : emptyStage, queueIndex: 0, stepIndex: 0 }
  }

  const handleLaunch = async ({ projectName, path, selected, bundleTier }) => {
    noteDraftEdit()
    const structurallySame =
      state.draftId &&
      state.path === path &&
      (path === "bundle" ? state.bundleTier === bundleTier : sameSet(state.selected, selected))

    if (structurallySame) {
      setState((prev) => ({ ...prev, projectName, ...goAfterLaunch(prev) }))
      return
    }

    setLaunching(true)
    setLaunchError(null)

    try {
      const projectPosting =
        path === "bundle"
          ? { project_name: projectName, bundle: true, bundle_tier: bundleTier }
          : { project_name: projectName, ...Object.fromEntries(selected.map((k) => [k, true])) }

      const draft = await createDraft({ project_posting: projectPosting })
      const hydrated = hydrateFromDraft(draft, SERVICE_CONFIGS) || { ...EMPTY_STATE }

      // Lock the manuscript to fiction for the Ultimate Fiction bundle (the toggle is
      // hidden in this flow), so the tier list + style/genre fields render correctly.
      const forceFiction = path === "bundle" && bundleComposition(bundleTier).forceFiction
      const seeded = forceFiction
        ? {
            ...hydrated,
            postings: {
              ...hydrated.postings,
              book_writing: { ...(hydrated.postings.book_writing || {}), fiction: true },
            },
          }
        : hydrated

      setState({ ...seeded, projectName, discount: draft.discount ?? null, ...goAfterLaunch(seeded) })

      if (draft.id) window.history.replaceState({}, "", `/post/${draft.id}`)
    } catch (err) {
      setLaunchError(err.message)
    } finally {
      setLaunching(false)
    }
  }

  // ── service stage navigation (hub-and-spoke) ────────────────────────────────
  // The customer enters one service from the hub, walks its own per-service steps,
  // and returns to the hub when done (or when stepping back off the first step).

  const svc = queue[Math.min(state.queueIndex, Math.max(queue.length - 1, 0))]
  const values = (svc && state.postings[svc]) || {}
  const steps = svc ? getServiceSteps(svc, values, ctx) : []
  const safeStepIndex = Math.min(state.stepIndex, Math.max(steps.length - 1, 0))
  const activeStep = steps[safeStepIndex]
  const visibleFields = activeStep ? visibleFieldsOf(activeStep, values, ctx) : []
  const stepValid = activeStep ? stepIsValid(activeStep, values, ctx) : true

  const isLastStep = safeStepIndex >= steps.length - 1

  const goToStep = (index) => {
    if (upgradePendingRef.current) return
    setState((prev) => ({ ...prev, stepIndex: index }))
  }

  const handleServiceContinue = () => {
    if (upgradePendingRef.current) return
    // Design idiom: Continue stays clickable; an invalid step reveals the
    // per-field errors instead of advancing (WI-A20).
    if (!stepValid) {
      setShowErr(true)
      const firstInvalid = visibleFields.find((field) => !fieldOk(field, values, ctx))
      if (firstInvalid?.key) {
        window.requestAnimationFrame(() => focusPostField(fieldNodesRef.current, firstInvalid.key))
      }
      return
    }

    if (!isLastStep) {
      setState((prev) => ({ ...prev, stepIndex: safeStepIndex + 1 }))
      return
    }

    // Last step of this service: hand control back to wherever we came from
    // (the hub on a normal walk, Review when the customer jumped here to edit).
    setState((prev) => ({ ...prev, stage: prev.returnStage || "hub", stepIndex: 0, returnStage: "hub" }))
  }

  const handleServiceBack = () => {
    if (upgradePendingRef.current) return
    if (safeStepIndex > 0) {
      setState((prev) => ({ ...prev, stepIndex: safeStepIndex - 1 }))
      return
    }

    setState((prev) => ({ ...prev, stage: prev.returnStage || "hub", returnStage: "hub" }))
  }

  // ── bundle materials (collected inline on the hub for a bundle) ──────────────

  const setExtra = (key, value) => {
    noteDraftEdit()
    setState((prev) => ({ ...prev, bundleExtras: { ...(prev.bundleExtras || {}), [key]: value } }))
  }

  const setFile = (slot, file) => {
    noteDraftEdit()
    noteBundleFileChange(slot, file)
    setState((prev) => ({
      ...prev,
      bundleExtras: {
        ...(prev.bundleExtras || {}),
        files: { ...((prev.bundleExtras || {}).files || {}), [slot]: file },
      },
    }))
  }

  // ── "Set up your services" hub ──────────────────────────────────────────────

  // A service is "configured" once every visible step's required fields are
  // satisfied — the same client gate the per-service Continue uses.
  const serviceConfigured = (key) => {
    const v = state.postings[key] || {}
    return getServiceSteps(key, v, ctx).every((step) => stepIsValid(step, v, ctx))
  }

  const allConfigured = queue.every((key) => serviceConfigured(key))
  const bundleMaterialsValid = !state.bundle || bundleDeliverablesValid(state)
  const hubReady = allConfigured && bundleMaterialsValid

  // Enter one service's wizard. returnStage records where finishing/backing-out of
  // that service should land: the hub on a normal walk, "review" when the customer
  // jumped in from the Review screen's per-service Edit link.
  const enterService = (index, returnStage = "hub") => {
    noteDraftEdit()
    setState((prev) => {
      const key = queue[index]
      const config = key ? SERVICE_CONFIGS[key] : null
      const defaults = config?.defaults
      // Seed the config's design defaults the first time a service is entered so
      // they become real submitted values (serialize only sends keys present in
      // state). Existing / resumed values win — defaults only fill gaps. Gap-fill
      // is empty-aware (mergeDefaults): hydrateFromDraft pre-populates the sub-state
      // from the fresh draft's null/empty columns, and those empty values must NOT
      // clobber a design default (a plain `...existing` spread would).
      const nextPostings =
        key && defaults
          ? { ...prev.postings, [key]: mergeDefaults(defaults, prev.postings[key] || {}) }
          : prev.postings
      return {
        ...prev,
        stage: "service",
        queueIndex: index,
        stepIndex: 0,
        returnStage,
        postings: nextPostings,
      }
    })
  }

  const handleHubSetup = (index) => enterService(index, "hub")

  const canCopyBookDetails = (targetKey) => {
    if (targetKey === "book_writing" || !canCopyBookDetailsTo(targetKey)) return false
    if (!queue.includes("book_writing") || !serviceConfigured("book_writing")) return false

    return copyBookDetailsSnapshot(
      state.postings.book_writing || {},
      state.postings[targetKey] || {},
      targetKey
    ).copied.length > 0
  }

  const handleCopyBookDetails = (targetKey) => {
    noteDraftEdit()
    setState((prev) => {
      const result = copyBookDetailsSnapshot(
        prev.postings.book_writing || {},
        prev.postings[targetKey] || {},
        targetKey
      )
      if (!result.copied.length) return prev

      return {
        ...prev,
        postings: { ...prev.postings, [targetKey]: result.posting },
      }
    })
  }

  const handleHubBack = () => {
    setState((prev) => ({ ...prev, stage: prev.returnStage === "review" ? "review" : "entry", returnStage: "hub" }))
  }

  const handleHubContinue = () => {
    if (!hubReady) return
    setState((prev) => ({ ...prev, stage: prev.returnStage === "review" ? "review" : hasAddon ? "addon" : "review", returnStage: "hub" }))
  }

  // ── add-on stage navigation ─────────────────────────────────────────────────

  const handleAddonBack = () => {
    setState((prev) => ({
      ...prev,
      stage: prev.returnStage === "review" ? "review" : queue.length === 0 ? "entry" : "hub",
      returnStage: "hub",
    }))
  }

  const addonValid = addonIsValid(state)
  const handleAddonContinue = () => {
    if (!addonValid) return
    setState((prev) => ({ ...prev, stage: "review", returnStage: "hub" }))
  }

  // ── review stage (per-service / management Edit jump-backs) ──────────────────
  // The Review screen's Edit links reuse the hub-and-spoke navigation: jump into a
  // service (or the add-on step) with returnStage = "review" so finishing the edit
  // lands the customer back on Review rather than the hub.

  const handleReviewEditService = (index) => enterService(index, "review")

  const fixTargetFor = (issue) => resolveIssueTarget(
    issue, state, ctx, queue, getServiceSteps, visibleFieldsOf, hasAddon
  )

  const handleFixSubmitIssue = (issue) => {
    const target = fixTargetFor(issue)
    if (!target) return

    pendingFieldFocusRef.current = target.focusKey || null
    setState((prev) => ({
      ...prev,
      stage: target.stage,
      queueIndex: target.queueIndex ?? prev.queueIndex,
      stepIndex: target.stepIndex ?? prev.stepIndex,
      returnStage: "review",
    }))
  }

  const handleReviewEditManagement = () => {
    setState((prev) => ({ ...prev, stage: "addon", returnStage: "review" }))
  }

  // ── review stage ────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!state.draftId || explicitSaveRef.current || upgradePendingRef.current || discountBusyRef.current) return

    explicitSaveRef.current = true
    setSubmitting(true)
    setSubmitErrors(null)
    setSubmitIssues([])
    setSubmitFinishProfile(null)

    try {
      await saveCurrentDraft({ requireLatest: true })
    } catch {
      explicitSaveRef.current = false
      setSubmitting(false)
      return
    }

    if (upgradePendingRef.current) {
      explicitSaveRef.current = false
      setSubmitting(false)
      return
    }

    try {
      const result = await submitProject(state.draftId)
      // Success: render the design's posted-confirmation view (WI-B2) from the
      // server payload. Fall back to the legacy redirect if the payload lacks
      // the posted marker (an older server shape or a bare { redirect }).
      if (result?.posted) {
        setPosted(result)
        setState((prev) => ({ ...prev, stage: "posted" }))
      } else {
        router.visit(result?.redirect || "/projects")
      }
    } catch (err) {
      // 422 (or create_order failure): stay on review, surface the errors. Newer
      // responses retain service/field context; the flat list remains the
      // compatibility fallback. A 403 for missing billing details also names
      // the finish-profile page, which returns here once the profile is saved.
      const list = Array.isArray(err?.data?.errors) && err.data.errors.length
        ? err.data.errors
        : [err.message]
      const issues = Array.isArray(err?.data?.issues) ? err.data.issues : []
      setSubmitErrors(list.reduce((acc, msg, i) => ({ ...acc, [i]: msg }), {}))
      setSubmitIssues(issues)
      setSubmitFinishProfile(err?.data?.finishProfile || null)
      explicitSaveRef.current = false
      setSubmitting(false)
    }
  }

  const handleApplyDiscount = async (code) => {
    if (!state.draftId || explicitSaveRef.current || upgradePendingRef.current || discountBusyRef.current) return
    discountBusyRef.current = true
    setDiscountBusy(true)
    setDiscountError(null)

    try {
      const draft = await addDiscount(state.draftId, code)
      setState((prev) => ({ ...prev, discount: draft.discount ?? null }))
      if (!draft.discount) setDiscountError("That code could not be applied.")
    } catch (err) {
      setDiscountError(err.message)
    } finally {
      discountBusyRef.current = false
      setDiscountBusy(false)
    }
  }

  const handleRemoveDiscount = async () => {
    if (!state.draftId || explicitSaveRef.current || upgradePendingRef.current || discountBusyRef.current) return
    discountBusyRef.current = true
    setDiscountBusy(true)
    setDiscountError(null)

    try {
      const draft = await removeDiscount(state.draftId)
      setState((prev) => ({ ...prev, discount: draft.discount ?? null }))
    } catch (err) {
      setDiscountError(err.message)
    } finally {
      discountBusyRef.current = false
      setDiscountBusy(false)
    }
  }

  // The upgrade is saved before the wizard shows it. The server persists the
  // Platinum tier, its outline child and the detached own outline in one
  // transaction and keeps the applied code only if it covers Platinum, so the
  // wizard adopts the tier and discount it answers with. A refusal (422) rolled
  // everything back; any other failure leaves the outcome unknown, so the wizard
  // stays held and reloads the draft from the server.
  const upgradeToPlatinum = async () => {
    const current = stateRef.current
    if (!current.draftId || explicitSaveRef.current || discountBusyRef.current) return

    upgradePendingRef.current = true
    setUpgradePending(true)

    try {
      const draft = await saveCurrentDraft({ snapshot: platinumUpgradeOf(current) })
      setState((prev) => ({
        ...platinumUpgradeOf(prev),
        bundleTier: draft?.bundleTier ?? PLATINUM,
        discount: draft?.discount ?? null,
        // The stored server total priced Silver/Gold; the Platinum quote replaces it.
        serverCost: null,
      }))
      if (current.discount && !draft?.discount) setDiscountError("That code could not be applied.")
    } catch (err) {
      if (err?.status !== 422) {
        router.visit(`/post/${current.draftId}`)
        return
      }
    }

    upgradePendingRef.current = false
    setUpgradePending(false)
  }

  // ── shared: Save & exit ─────────────────────────────────────────────────────

  const handleSaveExit = async () => {
    if (explicitSaveRef.current || upgradePendingRef.current || discountBusyRef.current) return
    if (state.draftId) {
      explicitSaveRef.current = true
      setSavingExit(true)
      try {
        await saveCurrentDraft({ requireLatest: true })
      } catch {
        explicitSaveRef.current = false
        setSavingExit(false)
        return
      }
    }
    router.visit("/projects")
  }

  // ── render ──────────────────────────────────────────────────────────────────

  // Show the split entry when the customer has drafts and hasn't chosen "Start
  // something new", with no deep-link prefill and no by-id resume in flight (the
  // controller sends an empty `drafts` on those, but guard here too). Otherwise
  // the entry stage renders the EntryPicker directly.
  const showSplit =
    drafts.length > 0 && !splitDismissed && !prefill && !catalog?.draft

  const railProps = buildRailProps(state, ctx, catalog, queue)

  const bundleName = state.bundle
    ? (catalog.bundles || []).find((b) => b.bundleTier === state.bundleTier)?.name || "bundle"
    : null

  // editIndex = the service's position in the queue, so the Review screen's Edit
  // link can jump straight back into it via the same handler the hub uses.
  const configuredRows = queue.map((key, i) => ({
    key,
    name: SERVICE_CONFIGS[key]?.name || key,
    detail: serviceDetail(state.postings[key] || {}),
    fields: serviceReviewFields(key, state.postings[key] || {}, ctx),
    editIndex: i,
  }))

  // The QA/QC add-ons are only offered for QA/QC-eligible selections AND only to
  // managed-services customers (the server gates QA/QC on the same flag — bug 11);
  // the Review screen mirrors the add-on step and shows those rows under this gate.
  const eligibleQaQc = isQaQcEligible(state.selected) && hasAddon

  // For a bundle the review lists the configured manuscript + cover, then the
  // included deliverables (outline/description/formatting/narration) as rows.
  const summaryRows = state.bundle
    ? [
        ...configuredRows,
        ...bundleDeliverables(state, ctx)
          .filter((d) => d.key !== "cover")
          .map((d) => ({
            key: `deliv-${d.key}`,
            name: d.title,
            detail: d.priced ? "Included · priced by length" : "Included",
          })),
      ]
    : configuredRows

  return (
    <>
      <div ref={wizardTopRef} className="mx-auto flex max-w-[1040px] flex-col gap-6 px-8 pt-8 pb-14">
        {/* band */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-border bg-card px-5 py-4">
          <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-border bg-secondary">
            <Feather className="size-[21px] text-foreground" strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.09em] text-muted-foreground">Post a project</div>
            <h1 className="truncate font-display text-[18px] font-bold tracking-tight text-foreground">
              {state.projectName || "New project"}
            </h1>
          </div>
          {state.draftId && savedAt && !saveError && (
            <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
              <Check className="size-3.5 text-done" strokeWidth={2.4} />
              Draft saved
            </span>
          )}
        </div>

        {state.draftId && saveError && state.stage !== "posted" && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
            <span className="min-w-0 flex-1">Draft not saved: {saveError}</span>
            <button type="button" onClick={retrySave} disabled={retryingSave || submitting || savingExit} className="font-semibold underline underline-offset-2 disabled:opacity-50">
              {retryingSave ? "Retrying…" : "Retry save"}
            </button>
          </div>
        )}

        {state.stage === "entry" && showSplit && (
          <EntrySplit drafts={drafts} onStartNew={() => setSplitDismissed(true)} />
        )}

        {state.stage === "entry" && !showSplit && (
          <EntryPicker
            catalog={catalog}
            initial={{
              projectName: state.projectName,
              path: state.path,
              selected: state.selected,
              bundleTier: state.bundleTier,
            }}
            focusedService={state.focusedService}
            repeat={state.repeat}
            onLaunch={handleLaunch}
            onSaveExit={handleSaveExit}
            buildRail={(pseudo) => buildRailProps(pseudo, ctx, catalog, queueFromSelected(pseudo.selected || []))}
            launching={launching}
            error={launchError}
          />
        )}

        {state.stage === "hub" && (
          <WizardShell
            steps={[
              { id: "path", title: "Path" },
              { id: "select", title: state.bundle ? "Bundle" : "Packages" },
              { id: "setup", title: "Set up" },
            ]}
            activeIndex={2}
            completedThrough={1}
            stepperCollapseAt={STEPPER_COLLAPSE.picker}
            onJump={(i) => i < 2 && handleHubBack()}
            stepLabel={state.projectName || "Untitled project"}
            title={state.bundle ? `Set up your ${bundleName}` : "Set up your services"}
            sub={
              state.bundle
                ? "Start with the manuscript, the rest of the bundle is handled by your team. Nothing is charged until a writer is attached."
                : "Configure each service in turn. You can come back to this list anytime, your selections are saved as a draft."
            }
            onBack={handleHubBack}
            backLabel={state.returnStage === "review" ? "Back to review" : "Back to packages"}
            onSaveExit={handleSaveExit}
            onContinue={handleHubContinue}
            continueLabel={state.returnStage === "review" || !hasAddon ? "Continue to review" : "Continue to add-ons"}
            continueDisabled={!hubReady}
            footerNote={!hubReady ? "Set up each service to continue." : undefined}
            priceRail={<PriceRail {...railProps} />}
          >
            <ServiceSetupHub
              catalog={catalog}
              state={state}
              ctx={ctx}
              queue={queue}
              configured={serviceConfigured}
              canCopyBookDetails={canCopyBookDetails}
              onCopyBookDetails={handleCopyBookDetails}
              onSetup={handleHubSetup}
              setExtra={setExtra}
              setFile={setFile}
              uploadStatuses={bundleUploadStatuses}
              retryUpload={retrySave}
              retryingUpload={retryingSave}
            />
          </WizardShell>
        )}

        {state.stage === "service" && activeStep && (
          <WizardShell
            title={activeStep.title}
            sub={activeStep.sub}
            steps={steps.map((s) => ({ id: s.id, title: s.title, label: s.node }))}
            activeIndex={safeStepIndex}
            completedThrough={safeStepIndex - 1}
            stepperCollapseAt={
              svc === "book_writing" ? STEPPER_COLLAPSE.bookWriting : STEPPER_COLLAPSE.service
            }
            onJump={goToStep}
            onBack={handleServiceBack}
            backDisabled={upgradePending}
            backLabel={safeStepIndex === 0 ? (state.returnStage === "review" ? "Back to review" : "Back to setup") : "Back"}
            onSaveExit={upgradePending ? undefined : handleSaveExit}
            onContinue={handleServiceContinue}
            continueLabel={isLastStep ? "Save & continue" : "Continue"}
            continueDisabled={upgradePending}
            footerNote={
              showErr && !stepValid ? "Complete the required fields on this step to continue." : undefined
            }
            priceRail={<PriceRail {...railProps} />}
          >
            {visibleFields.map((field, i) => {
              const fieldKey = field.key || `${field.type}-${i}`
              const error = showErr ? fieldError(field, values, ctx) : null

              return (
                <div
                  key={fieldKey}
                  ref={(node) => {
                    if (node) fieldNodesRef.current.set(fieldKey, node)
                    else fieldNodesRef.current.delete(fieldKey)
                  }}
                  data-post-field={fieldKey}
                  data-validation-error={error ? "true" : undefined}
                  className="scroll-mt-6"
                >
                  <FieldRenderer
                    field={field}
                    values={values}
                    setField={(key, value) => setPosting(svc, key, value)}
                    error={error}
                    ctx={ctx}
                  />
                </div>
              )
            })}
          </WizardShell>
        )}

        {state.stage === "addon" && hasAddon && (
          <WizardShell
            title="Management & review"
            sub="Choose how hands-on you want to be, and whether to add quality checks. Add-ons must be chosen now, before the project is created."
            steps={[{ id: "addon", title: "Management & review" }]}
            activeIndex={0}
            completedThrough={-1}
            stepperCollapseAt={STEPPER_COLLAPSE.addon}
            onBack={handleAddonBack}
            onSaveExit={handleSaveExit}
            onContinue={handleAddonContinue}
            continueLabel="Review"
            continueDisabled={!addonValid}
            footerNote={!addonValid ? "Complete the Managed questionnaire to continue." : undefined}
            priceRail={<PriceRail {...railProps} />}
          >
            <AddonStep
              state={state}
              setField={setAddonField}
              eligibleQaQc={eligibleQaQc}
              managedAllowed={managedAllowedFor(state.selected, state)}
              managedTiers={managedTiersFor(state.selected, state, ctx)}
              addonLines={addonLineAmounts(state, state.selected, ctx)}
              ctx={ctx}
            />
          </WizardShell>
        )}

        {state.stage === "review" && (
          <ReviewSummary
            state={state}
            summaryRows={summaryRows}
            eligibleQaQc={eligibleQaQc}
            showManagement={hasAddon}
            railProps={railProps}
            fileNotice={null}
            submitting={submitting}
            submitErrors={submitErrors}
            submitIssues={submitIssues}
            finishProfile={submitFinishProfile}
            onBack={() => setState((prev) => ({ ...prev, stage: hasAddon ? "addon" : "hub" }))}
            onSubmit={handleSubmit}
            onEditService={handleReviewEditService}
            onFixIssue={handleFixSubmitIssue}
            fixTargetFor={fixTargetFor}
            onEditManagement={handleReviewEditManagement}
            onApplyDiscount={handleApplyDiscount}
            onRemoveDiscount={handleRemoveDiscount}
            discountBusy={discountBusy}
            discountError={discountError}
          />
        )}

        {state.stage === "posted" && posted && (
          <PostedConfirmation
            payload={posted}
            projectName={state.projectName}
            tier={state.postings?.book_writing?.tier}
            onGoToProjects={() => router.visit(posted.projectsPath || "/projects")}
            onBrowse={(path) => router.visit(path || posted.browseWritersPath || "/find-freelancers")}
          />
        )}
      </div>
    </>
  )
}

PostProject.layout = (page) => (
  <AppShell active="post" crumb={["Projects", "Post a Project"]}>
    {page}
  </AppShell>
)
